import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import MapRoundedIcon from '@mui/icons-material/MapRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useT } from '../../i18n';
import './NavToAddress.css';

/**
 * Reusable navigation block for a provider address — Waze button + Google-Maps (car/transit/walk)
 * links + a "מפה" button that opens an embedded map in an overlay. Identical look to the appointment
 * details screen (shared class names). Renders nothing when there is no address.
 *
 * @param {string} address        the provider address (drives the Waze / Maps links)
 * @param {string} [phone]        provider phone (rendered as a tel: row above the address)
 * @param {string} [email]        provider email (rendered as a mailto: row)
 * @param {string} [location]     provider free-text note ("הערות") shown only if set
 * @param {boolean} [showAddress] show the "כתובת: …" row (default true)
 * @param {boolean} [bare]        drop the card chrome (border/shadow/padding) for nesting in a card
 */
export default function NavToAddress({ address, phone, email, location, showAddress = true, bare = false }) {
  // ⚠️ ההוקים קודמים ל-return המוקדם, אחרת סדר-ההוקים משתנה בין רינדורים.
  const t = useT();
  const [mapOpen, setMapOpen] = useState(false);
  if (!address && !phone && !email) return null;

  const mapsUrl = (mode) =>
    `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}&travelmode=${mode}`;
  const mapEmbedUrl = `https://www.google.com/maps?q=${encodeURIComponent(address)}&z=16&hl=iw&output=embed`;
  const wazeUrl = `https://waze.com/ul?q=${encodeURIComponent(address)}&navigate=yes`;

  // open the Waze APP first (waze://); fall back to the web link in a new tab if it isn't installed.
  const openWaze = (e) => {
    if (e) e.preventDefault();
    const q = encodeURIComponent(address);
    const fallback = setTimeout(() => { window.open(`https://waze.com/ul?q=${q}&navigate=yes`, '_blank', 'noopener'); }, 1400);
    document.addEventListener('visibilitychange', () => { if (document.hidden) clearTimeout(fallback); }, { once: true });
    window.location.href = `waze://?q=${q}&navigate=yes`;
  };

  return (
    <div className={'nta' + (bare ? ' nta--bare' : '')}>
      <div className="provider-contact-wrap">
        <div className="provider-contact-info">
          {phone && <div className="provider-contact-row">{t('common.phone')}: <a href={`tel:${phone}`} dir="ltr">{phone}</a></div>}
          {email && <div className="provider-contact-row">{t('common.email')}: <a href={`mailto:${email}`} dir="ltr">{email}</a></div>}
          {showAddress && address && <div className="provider-contact-row">{t('common.address')}: {address}</div>}
          {location && <div className="provider-contact-row">{t('common.notes')}: {location}</div>}
        </div>
        {address && (
          <a className="provider-waze-big" href={wazeUrl} onClick={openWaze} title={t('nav.wazeTitle')} aria-label={t('nav.wazeTitle')}>
            <img src="/waze.svg" alt="Waze" className="provider-waze-img" />
            <span className="provider-waze-label">Waze</span>
          </a>
        )}
      </div>
      {address && (
      <div className="provider-maps">
        <div className="maps-nav-title">{t('nav.mapsTitle')}</div>
        <div className="maps-nav-links">
          <a className="maps-nav-link" target="_blank" rel="noopener noreferrer" href={mapsUrl('driving')}>
            <DirectionsCarIcon fontSize="small" /> {t('nav.car')}
          </a>
          <a className="maps-nav-link" target="_blank" rel="noopener noreferrer" href={mapsUrl('transit')}>
            <DirectionsBusIcon fontSize="small" /> {t('nav.transit')}
          </a>
          <a className="maps-nav-link" target="_blank" rel="noopener noreferrer" href={mapsUrl('walking')}>
            <DirectionsWalkIcon fontSize="small" /> {t('nav.walk')}
          </a>
          <button type="button" className="maps-nav-link maps-nav-mapbtn" onClick={() => setMapOpen(true)}>
            <MapRoundedIcon fontSize="small" /> {t('nav.map')}
          </button>
        </div>
      </div>
      )}

      {address && (
      <Dialog open={mapOpen} onClose={() => setMapOpen(false)} fullWidth maxWidth="sm" PaperProps={{ className: 'map-dialog-paper' }}>
        <div className="map-dialog-head">
          <span>{address}</span>
          <button type="button" className="map-dialog-close" aria-label={t('common.close')} onClick={() => setMapOpen(false)}>
            <CloseRoundedIcon fontSize="small" />
          </button>
        </div>
        {mapOpen && (
          <iframe title={t('nav.map')} className="map-dialog-frame" src={mapEmbedUrl} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
        )}
      </Dialog>
      )}
    </div>
  );
}
